import { nextTick, type ComputedRef, type Ref } from 'vue'

import type { UploadedWorkspaceVideo, StoryboardWorkspaceState } from './types'

interface SceneVideoTaskStatusResponse {
  workflow: any
  task?: Record<string, any> | null
  scene?: Record<string, any> | null
}

interface UploadedMaterial {
  id?: number
  name?: string
  file_url?: string
  updated_at?: string
}

interface SceneVideoWorkspaceOptions {
  projectId: number
  workflow: Ref<any>
  sceneVideoTaskMap: ComputedRef<Record<number, Record<string, any>>>
  sceneVideoGenerating: Ref<Record<number, boolean>>
  sceneVideoLoading: Ref<Record<number, boolean>>
  sceneVideoSaving: Ref<Record<number, boolean>>
  trackedSceneVideoTaskIds: Ref<Record<number, string>>
  generatedSceneVideoOverrides: Ref<Record<number, Record<string, any>>>
  sceneVideoPlayerErrors: Ref<Record<number, string>>
  sceneVideoFeedbackDrafts: Ref<Record<number, string>>
  sceneVideoUploadDialogVisible: Ref<boolean>
  sceneVideoUploadFeedbackDraft: Ref<string>
  pendingSceneVideoUploadNumber: Ref<number | null>
  activeSceneVideoNumber: Ref<number | null>
  sceneVideoUploadInput: Ref<HTMLInputElement | null>
  ensureStoryboardScenesReady: () => Promise<boolean>
  getStoryboardWorkspaceStatePayload: () => StoryboardWorkspaceState
  persistStoryboardWorkspaceState: (nextState: StoryboardWorkspaceState, options?: { successMessage?: string; silent?: boolean }) => Promise<void>
  getSceneVideoTaskStatusApi: (projectId: number, sceneNumber: number) => Promise<SceneVideoTaskStatusResponse>
  uploadMaterial: (formData: FormData, options: { project_id: number; material_type: string; description: string }) => Promise<UploadedMaterial>
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
  getActionErrorMessage: (error: any) => string | null
}

export function useSceneVideoWorkspace(options: SceneVideoWorkspaceOptions) {
  const sceneVideoTaskPollTimers = new Map<number, number>()
  const sceneVideoTaskPollInFlight = new Set<number>()

  function getSceneVideoTaskStatus(sceneNumber: number) {
    return String(options.sceneVideoTaskMap.value[sceneNumber]?.status || '').trim().toLowerCase()
  }

  function clearSceneVideoPlaybackError(sceneNumber: number) {
    const next = { ...options.sceneVideoPlayerErrors.value }
    delete next[sceneNumber]
    options.sceneVideoPlayerErrors.value = next
  }

  function handleSceneVideoLoadedData(sceneNumber: number) {
    clearSceneVideoPlaybackError(sceneNumber)
  }

  function handleSceneVideoPlaybackError(sceneNumber: number) {
    options.sceneVideoPlayerErrors.value = {
      ...options.sceneVideoPlayerErrors.value,
      [sceneNumber]: '当前镜头视频链接不可访问，或浏览器没有正确解码这段视频。常见原因是 OSS 素材失效、权限关闭，或旧结果编码兼容性较差。',
    }
  }

  function isSceneVideoTaskActive(sceneNumber: number) {
    const status = getSceneVideoTaskStatus(sceneNumber)
    return status === 'queued' || status === 'running'
  }

  function isSceneVideoGenerating(sceneNumber: number) {
    return Boolean(options.sceneVideoGenerating.value[sceneNumber]) || isSceneVideoTaskActive(sceneNumber)
  }

  function stopSceneVideoTaskPolling(sceneNumber: number) {
    const timer = sceneVideoTaskPollTimers.get(sceneNumber)
    if (timer !== undefined) {
      window.clearInterval(timer)
      sceneVideoTaskPollTimers.delete(sceneNumber)
    }
  }

  function stopAllSceneVideoTaskPolling() {
    Array.from(sceneVideoTaskPollTimers.keys()).forEach((sceneNumber) => {
      stopSceneVideoTaskPolling(sceneNumber)
    })
  }

  function clearTrackedSceneVideoTask(sceneNumber: number, taskId?: string) {
    const currentTaskId = options.trackedSceneVideoTaskIds.value[sceneNumber]
    if (!currentTaskId || (taskId && currentTaskId !== taskId)) {
      return
    }

    const next = { ...options.trackedSceneVideoTaskIds.value }
    delete next[sceneNumber]
    options.trackedSceneVideoTaskIds.value = next
  }

  async function refreshSceneVideoTaskStatus(sceneNumber: number) {
    if (sceneVideoTaskPollInFlight.has(sceneNumber)) {
      return
    }

    sceneVideoTaskPollInFlight.add(sceneNumber)
    try {
      const response = await options.getSceneVideoTaskStatusApi(options.projectId, sceneNumber)
      options.workflow.value = response.workflow

      const status = String(response.task?.status || '').trim().toLowerCase()
      const taskId = String(response.task?.task_id || '').trim()

      if (response.scene && status === 'succeeded') {
        options.generatedSceneVideoOverrides.value = {
          ...options.generatedSceneVideoOverrides.value,
          [sceneNumber]: response.scene,
        }
      }

      if (status === 'queued' || status === 'running') {
        startSceneVideoTaskPolling(sceneNumber)
        return
      }

      stopSceneVideoTaskPolling(sceneNumber)
      if (taskId && options.trackedSceneVideoTaskIds.value[sceneNumber] === taskId) {
        if (status === 'succeeded') {
          options.success(`镜头 ${sceneNumber} 视频已生成`)
        } else if (status === 'failed' || status === 'cancelled') {
          options.error(String(response.task?.error || '分镜视频生成失败'))
        }
        clearTrackedSceneVideoTask(sceneNumber, taskId)
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        stopSceneVideoTaskPolling(sceneNumber)
        clearTrackedSceneVideoTask(sceneNumber)
      }
    } finally {
      sceneVideoTaskPollInFlight.delete(sceneNumber)
    }
  }

  function startSceneVideoTaskPolling(sceneNumber: number) {
    if (sceneVideoTaskPollTimers.has(sceneNumber)) {
      return
    }

    const timer = window.setInterval(() => {
      void refreshSceneVideoTaskStatus(sceneNumber)
    }, 3000)
    sceneVideoTaskPollTimers.set(sceneNumber, timer)
  }

  function syncTaskPolling(taskMap: Record<number, Record<string, any>>) {
    const activeSceneNumbers = new Set<number>()

    Object.entries(taskMap).forEach(([sceneKey, task]) => {
      const sceneNumber = Number(sceneKey)
      const status = String(task?.status || '').trim().toLowerCase()
      if (!sceneNumber) {
        return
      }

      if (status === 'queued' || status === 'running') {
        activeSceneNumbers.add(sceneNumber)
        startSceneVideoTaskPolling(sceneNumber)
        return
      }

      stopSceneVideoTaskPolling(sceneNumber)
      if ((status === 'succeeded' || status === 'failed' || status === 'cancelled') && options.trackedSceneVideoTaskIds.value[sceneNumber]) {
        void refreshSceneVideoTaskStatus(sceneNumber)
      }
    })

    Array.from(sceneVideoTaskPollTimers.keys()).forEach((sceneNumber) => {
      if (!activeSceneNumbers.has(sceneNumber) && !options.trackedSceneVideoTaskIds.value[sceneNumber]) {
        stopSceneVideoTaskPolling(sceneNumber)
      }
    })
  }

  function triggerSceneVideoUpload(sceneNumber: number) {
    options.pendingSceneVideoUploadNumber.value = sceneNumber
    options.sceneVideoUploadFeedbackDraft.value = options.sceneVideoFeedbackDrafts.value[sceneNumber] || ''
    options.sceneVideoUploadDialogVisible.value = true
  }

  function handleCancelSceneVideoUploadDialog() {
    options.sceneVideoUploadDialogVisible.value = false
    options.pendingSceneVideoUploadNumber.value = null
    options.sceneVideoUploadFeedbackDraft.value = ''
  }

  async function handleConfirmSceneVideoUploadDialog() {
    const sceneNumber = options.pendingSceneVideoUploadNumber.value
    if (sceneNumber === null) {
      return
    }

    const ready = await options.ensureStoryboardScenesReady()
    if (!ready) {
      return
    }

    options.sceneVideoFeedbackDrafts.value = {
      ...options.sceneVideoFeedbackDrafts.value,
      [sceneNumber]: options.sceneVideoUploadFeedbackDraft.value,
    }
    options.sceneVideoUploadDialogVisible.value = false
    options.activeSceneVideoNumber.value = sceneNumber
    await nextTick()
    if (options.sceneVideoUploadInput.value) {
      options.sceneVideoUploadInput.value.value = ''
      options.sceneVideoUploadInput.value.click()
    }
  }

  async function handleSceneVideoFileSelected(event: Event) {
    const target = event.target as HTMLInputElement | null
    const file = target?.files?.[0]
    const sceneNumber = options.activeSceneVideoNumber.value

    if (!file || sceneNumber === null) {
      return
    }

    options.sceneVideoLoading.value = { ...options.sceneVideoLoading.value, [sceneNumber]: true }
    try {
      const formData = new FormData()
      formData.append('file', file)
      const material = await options.uploadMaterial(formData, {
        project_id: options.projectId,
        material_type: 'video_clip',
        description: `storyboard-video-${sceneNumber}`,
      })

      const videoUrl = String(material.file_url || '').trim()
      if (!videoUrl) {
        throw new Error('上传成功，但未返回视频地址')
      }

      const current = options.getStoryboardWorkspaceStatePayload()
      const sceneUploadedVideos = { ...(current.scene_uploaded_videos || {}) }
      sceneUploadedVideos[String(sceneNumber)] = {
        url: videoUrl,
        name: material.name,
        material_id: material.id,
        feedback: options.sceneVideoFeedbackDrafts.value[sceneNumber] || '',
        uploaded_at: material.updated_at,
      } satisfies UploadedWorkspaceVideo

      await options.persistStoryboardWorkspaceState({
        ...current,
        scene_video_feedbacks: {
          ...(current.scene_video_feedbacks || {}),
          [String(sceneNumber)]: options.sceneVideoFeedbackDrafts.value[sceneNumber] || '',
        },
        scene_uploaded_videos: sceneUploadedVideos,
      }, { successMessage: `镜头 ${sceneNumber} 视频已上传` })
    } catch (error: any) {
      options.error(error?.response?.data?.detail || error?.response?.data?.message || error?.message || '上传视频失败')
    } finally {
      options.sceneVideoLoading.value = { ...options.sceneVideoLoading.value, [sceneNumber]: false }
      options.activeSceneVideoNumber.value = null
      options.pendingSceneVideoUploadNumber.value = null
      options.sceneVideoUploadFeedbackDraft.value = ''
      if (target) {
        target.value = ''
      }
    }
  }

  async function handleSaveSceneVideoFeedback(sceneNumber: number) {
    options.sceneVideoSaving.value = { ...options.sceneVideoSaving.value, [sceneNumber]: true }
    try {
      const ready = await options.ensureStoryboardScenesReady()
      if (!ready) {
        return
      }
      const current = options.getStoryboardWorkspaceStatePayload()
      await options.persistStoryboardWorkspaceState({
        ...current,
        scene_video_feedbacks: {
          ...(current.scene_video_feedbacks || {}),
          [String(sceneNumber)]: options.sceneVideoFeedbackDrafts.value[sceneNumber] || '',
        },
      }, { successMessage: `镜头 ${sceneNumber} 视频说明已保存` })
    } catch (error: any) {
      options.error(options.getActionErrorMessage(error) || '保存镜头视频说明失败')
    } finally {
      options.sceneVideoSaving.value = { ...options.sceneVideoSaving.value, [sceneNumber]: false }
    }
  }

  return {
    clearSceneVideoPlaybackError,
    clearTrackedSceneVideoTask,
    getSceneVideoTaskStatus,
    handleCancelSceneVideoUploadDialog,
    handleConfirmSceneVideoUploadDialog,
    handleSaveSceneVideoFeedback,
    handleSceneVideoFileSelected,
    handleSceneVideoLoadedData,
    handleSceneVideoPlaybackError,
    isSceneVideoGenerating,
    isSceneVideoTaskActive,
    refreshSceneVideoTaskStatus,
    startSceneVideoTaskPolling,
    stopAllSceneVideoTaskPolling,
    stopSceneVideoTaskPolling,
    syncTaskPolling,
    triggerSceneVideoUpload,
  }
}